import { FUNNEL_STATE_QUERY_KEY } from '../utils/funnelStateCode';
import {
  LAST_VISITED_PAGE_V2_COOKIE_KEY,
  type LastVisitedPageV2CookieModel,
} from '../utils/saveRoutesCookie';

export const isAgeGateNavigating = ref(false);

let _resolveAgeGate: (() => void) | null = null;
// eslint-disable-next-line import/no-mutable-exports
export let ageGatePending: Promise<void> | null = null;

function stripLocalePrefix(path: string): string {
  const match = path.match(/^\/([A-Z]{2}(?:-[A-Z]{2})?)(\/.*)?$/i);
  return match ? (match[2] || '/') : '/';
}

export function useAgeGate() {
  if (!import.meta.client) return;

  const nuxtApp = useNuxtApp();
  const { $closeLoader } = nuxtApp;
  const { trackQuizStarted } = useEventsDispatcher();
  const store = useKegelAnalyticStore();
  const localePath = useLocalePath();
  const router = useRouter();
  const route = useRoute();
  const ageItems = (useAppConfig() as any).homePage as Array<{
    id: number
    name: string
    group: string
    compatibilityScore: string
  }>;

  const selectedAgeBtnCookie = useCookie<string | null>('selectedAgeBtn', {
    default: () => null,
  });

  function clearSelectedAge(): void {
    selectedAgeBtnCookie.value = null;
    delete (globalThis as any).__aoSelectedAgeId;
  }

  router.beforeEach((to) => {
    if (isAgeGateNavigating.value && !to.path.includes('/quiz/')) {
      return false;
    }
  });

  async function handleCloseLoader(selectedAgeId: string) {
    const ageId = Number(selectedAgeId);
    isAgeGateNavigating.value = true;
    ageGatePending = new Promise<void>((resolve) => {
      _resolveAgeGate = resolve;
    });
    try {
      const age = ageItems.find(item => item.id === ageId) ?? null;

      store.ageId = String(ageId);

      await trackQuizStarted(age?.group);
      clearSelectedAge();
      await new Promise<void>((resolve) => {
        const observer = new MutationObserver(() => {
          if (document.getElementById('quiz-question')) {
            observer.disconnect();
            resolve();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        router.push(
          localePath({
            path: '/quiz/0/question',
            query: { ...route.query, uuid: store.userUuid, [FUNNEL_STATE_QUERY_KEY]: store.funnelCode },
          }),
        );
      });
      $closeLoader();
    } finally {
      isAgeGateNavigating.value = false;
      _resolveAgeGate?.();
      _resolveAgeGate = null;
      ageGatePending = null;
    }
  }

  onMounted(() => {
    const cookiePage = useCookie<string>('lastVisitedPage', { default: () => '', maxAge: 3600 });
    const cookiePageV2 = useCookie<LastVisitedPageV2CookieModel | null>(LAST_VISITED_PAGE_V2_COOKIE_KEY, { default: () => null, maxAge: 3600 });
    const funnelPrefix = useFunnelPrefix();
    const lastBase = stripLocalePrefix(stripFunnelPrefix(cookiePage.value, funnelPrefix));
    const currentFunnelName = useFunnelName(route.query.funnel);
    const hasValidV2Route = Boolean(
      cookiePageV2.value?.funnelName === currentFunnelName
      && cookiePageV2.value?.lastRoute
      && stripLocalePrefix(stripFunnelPrefix(cookiePageV2.value.lastRoute, funnelPrefix)) !== '/',
    );
    const hasLegacyRoute = Boolean(lastBase && lastBase !== '/');
    const selectedAgeId = selectedAgeBtnCookie.value || (globalThis as any).__aoSelectedAgeId as string | undefined;

    if (hasValidV2Route || hasLegacyRoute) {
      clearSelectedAge();
      $closeLoader();
    } else if (selectedAgeId) {
      handleCloseLoader(selectedAgeId);

      return;
    }

    const onAgeSelected = (e: Event) => {
      document.removeEventListener('ao:age-selected', onAgeSelected);
      handleCloseLoader((e as CustomEvent<{ ageId: string }>).detail.ageId);
    };

    document.addEventListener('ao:age-selected', onAgeSelected);

    onUnmounted(() => {
      document.removeEventListener('ao:age-selected', onAgeSelected);
    });
  });
}
